import { useEffect, useState, useCallback } from 'react';
import { apiService } from '@services/api';
import logger from '@utils/logger';
import { NOTIFICATION_BADGE_REFRESH_EVENT } from '@utils/notificationBadgeBus';
import { useDemands } from '@contexts/DemandsContext';

/** Cada categoria vem `null` quando o papel do usuário não tem a permissão correspondente
 * (achado real, relato do usuário: "as opções ao clicar no sino são sempre exibidas
 * independente das permissões do papel") — `null` é "você não pode ver isto", diferente de `0`
 * ("sem pendências"). Ver NotificationBadgeService.ts (backend). */
export interface NotificationBadgeCounts {
  openCount: number | null;
  inProgressCount: number | null;
  overdueCount: number | null;
  pendingSuggestionsCount: number | null;
  pendingChecklistsCount: number | null;
  patrimonyDivergenceCount: number | null;
  whatsappPendingCount: number | null;
  total: number;
}

const REFETCH_INTERVAL_MS = 60_000;

/**
 * Contagens da Central de Notificações (sino, pedido do usuário 05/09) — mesmo padrão
 * useEffect+useState já usado no resto do web (sem introduzir react-query, ver useClientCatalog.ts),
 * com refetch periódico leve + refetch quando a janela ganha foco (o usuário pode ter resolvido
 * algo em outra aba/dispositivo) + refetch imediato quando uma mutação relevante acontece nesta
 * mesma aba (ver notificationBadgeBus.ts — bug real: mover uma demanda pra fora das condições
 * contadas só atualizava o total depois de um refresh manual).
 *
 * Bug real (relato do usuário): o sino sempre contava TODOS os condomínios permitidos, mas cada
 * atalho de clique filtrava só o(s) condomínio(s) selecionado(s) na tela — números que nunca
 * batiam sempre que o usuário tinha mais de 1 condomínio e não via "Todos" selecionado (ex.: sino
 * "18 abertas", filtro "15"). Passa o MESMO `selectedClients` do DemandsContext pro backend
 * (`NotificationBadgeService`), e refaz a busca sempre que essa seleção muda — trocar de
 * condomínio no cabeçalho agora atualiza o sino junto, sem esperar o polling de 60s.
 */
export function useNotificationBadgeCounts() {
  const { selectedClients } = useDemands();
  // Comparar pelo CONTEÚDO (join), não pela referência do array — `selectedClients` muda de
  // identidade a cada render do DemandsContext mesmo com o mesmo conteúdo, o que refaria a busca
  // a cada render se fosse dependência direta do useCallback abaixo.
  const selectedClientsKey = selectedClients.join(',');
  const [counts, setCounts] = useState<NotificationBadgeCounts | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = useCallback(async () => {
    try {
      const response = await apiService.getNotificationBadgeCounts(selectedClientsKey ? selectedClientsKey.split(',') : []);
      if (response.data.success) {
        setCounts(response.data.data);
        setError(null);
      } else {
        setError('Não foi possível carregar as notificações.');
      }
    } catch (err) {
      logger.error('Erro ao buscar contagens da Central de Notificações', err);
      setError('Não foi possível carregar as notificações.');
    } finally {
      setLoading(false);
    }
  }, [selectedClientsKey]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, REFETCH_INTERVAL_MS);
    const onFocus = () => fetchCounts();
    const onRelevantMutation = () => fetchCounts();
    window.addEventListener('focus', onFocus);
    window.addEventListener(NOTIFICATION_BADGE_REFRESH_EVENT, onRelevantMutation);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(NOTIFICATION_BADGE_REFRESH_EVENT, onRelevantMutation);
    };
  }, [fetchCounts]);

  return { counts, loading, error, refetch: fetchCounts };
}
